import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Loader2, Video, ImageIcon } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GalleryTab = () => {
  const { toast } = useToast();
  const [galleryItems, setGalleryItems] = useState([]);
  const [showAddGallery, setShowAddGallery] = useState(false);
  const [newGalleryItem, setNewGalleryItem] = useState({ 
    title: '', 
    description: '', 
    type: 'image' 
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const token = localStorage.getItem('krolbolt_token');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      if (response.ok) setGalleryItems(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFileUpload = async (file, isVideo = false) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/upload/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.url;
      } else {
        throw new Error('Error uploading file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({ title: 'Error', description: 'No se pudo subir el archivo', variant: 'destructive' });
      return null;
    }
  };

  const handleAddGalleryItem = async (e) => {
    e.preventDefault();

    if (!fileUrl) {
      toast({ title: 'Error', description: 'Debes subir un archivo primero', variant: 'destructive' });
      return;
    }

    if (newGalleryItem.type === 'video' && !thumbnailUrl) {
      toast({ title: 'Error', description: 'Los videos necesitan una imagen de portada', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API}/gallery`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newGalleryItem,
          image_url: fileUrl,
          thumbnail_url: newGalleryItem.type === 'video' ? thumbnailUrl : null
        })
      });

      if (response.ok) {
        toast({ title: 'Éxito', description: 'Trabajo agregado correctamente' });
        setNewGalleryItem({ title: '', description: '', type: 'image' });
        setFileUrl('');
        setThumbnailUrl('');
        setShowAddGallery(false);
        fetchGallery();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo agregar el trabajo', variant: 'destructive' });
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    if (!window.confirm('¿Eliminar este trabajo?')) return;

    try {
      const response = await fetch(`${API}/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Éxito', description: 'Trabajo eliminado correctamente' });
        fetchGallery();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar el trabajo', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Galería de Trabajos</h2>
        <Button 
          onClick={() => setShowAddGallery(!showAddGallery)} 
          className="bg-gradient-to-r from-cyan-600 to-emerald-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Agregar Trabajo
        </Button>
      </div>

      {showAddGallery && (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-6">
            <form onSubmit={handleAddGalleryItem} className="space-y-4">
              <div>
                <Label className="text-slate-300">Título *</Label>
                <Input 
                  value={newGalleryItem.title} 
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, title: e.target.value})} 
                  className="bg-slate-900/50 border-slate-700 text-white" 
                  required 
                />
              </div>

              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Textarea 
                  value={newGalleryItem.description} 
                  onChange={(e) => setNewGalleryItem({...newGalleryItem, description: e.target.value})} 
                  className="bg-slate-900/50 border-slate-700 text-white" 
                />
              </div>

              <div>
                <Label className="text-slate-300">Tipo de archivo</Label>
                <select 
                  value={newGalleryItem.type} 
                  onChange={(e) => {
                    setNewGalleryItem({...newGalleryItem, type: e.target.value});
                    setFileUrl('');
                    setThumbnailUrl('');
                  }}
                  className="w-full p-2 bg-slate-900/50 border border-slate-700 text-white rounded-md"
                >
                  <option value="image">Imagen (JPG, PNG)</option>
                  <option value="video">Video (MP4)</option>
                </select>
              </div>
              
              <div>
                <Label className="text-slate-300">
                  {newGalleryItem.type === 'video' ? 'Subir Video (MP4) *' : 'Subir Imagen (JPG, PNG) *'}
                </Label>
                <div className="flex gap-2 items-center mt-2">
                  <Input 
                    type="file" 
                    accept={newGalleryItem.type === 'video' ? 'video/mp4,video/quicktime' : 'image/jpeg,image/jpg,image/png'}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadingFile(true);
                        const url = await handleFileUpload(file, newGalleryItem.type === 'video');
                        if (url) {
                          setFileUrl(url);
                          toast({ title: 'Archivo subido exitosamente' });
                        }
                        setUploadingFile(false);
                      }
                    }}
                    className="bg-slate-900/50 border-slate-700 text-white"
                    disabled={uploadingFile}
                  />
                  {uploadingFile && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                  {fileUrl && <Badge className="bg-green-500/20 text-green-400">✓ Subido</Badge>}
                </div>
                {fileUrl && (
                  <div className="mt-2">
                    {newGalleryItem.type === 'video' ? (
                      <div className="flex items-center gap-2 text-cyan-400">
                        <Video className="h-8 w-8" />
                        <span className="text-sm">Video cargado</span>
                      </div>
                    ) : (
                      <img src={fileUrl} alt="Preview" className="h-32 w-32 object-cover rounded" />
                    )}
                  </div>
                )}
              </div>

              {newGalleryItem.type === 'video' && (
                <div>
                  <Label className="text-slate-300">Imagen de Portada para el Video (JPG, PNG) *</Label>
                  <div className="flex gap-2 items-center mt-2">
                    <Input 
                      type="file" 
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setUploadingThumbnail(true);
                          const url = await handleFileUpload(file, false);
                          if (url) {
                            setThumbnailUrl(url);
                            toast({ title: 'Portada subida exitosamente' });
                          }
                          setUploadingThumbnail(false);
                        }
                      }}
                      className="bg-slate-900/50 border-slate-700 text-white"
                      disabled={uploadingThumbnail}
                    />
                    {uploadingThumbnail && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                    {thumbnailUrl && <Badge className="bg-green-500/20 text-green-400">✓ Subido</Badge>}
                  </div>
                  {thumbnailUrl && (
                    <div className="mt-2">
                      <img src={thumbnailUrl} alt="Thumbnail preview" className="h-32 w-32 object-cover rounded" />
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={uploadingFile || uploadingThumbnail || !fileUrl} 
                  className="bg-gradient-to-r from-cyan-600 to-emerald-600"
                >
                  Guardar Trabajo
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddGallery(false);
                    setNewGalleryItem({ title: '', description: '', type: 'image' });
                    setFileUrl('');
                    setThumbnailUrl('');
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {galleryItems.map((item) => (
          <Card key={item.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <div className="aspect-video overflow-hidden bg-slate-900 relative">
              {item.type === 'video' ? (
                <>
                  <img 
                    src={item.thumbnail_url || item.image_url} 
                    alt={item.title} 
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Video className="h-12 w-12 text-white" />
                  </div>
                </>
              ) : (
                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
              )}
            </div>
            <CardHeader>
              <CardTitle className="text-white text-lg">{item.title}</CardTitle>
              {item.description && (
                <CardDescription className="text-slate-400">{item.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <Button 
                size="sm" 
                variant="destructive" 
                className="w-full" 
                onClick={() => handleDeleteGalleryItem(item.id)}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Eliminar
              </Button>
            </CardContent>
          </Card>
        ))}
        {galleryItems.length === 0 && (
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 col-span-full">
            <CardContent className="p-12 text-center">
              <ImageIcon className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No hay trabajos en la galería</p>
              <p className="text-slate-500 text-sm">Agrega imágenes o videos de tus trabajos</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GalleryTab;
